// Badge types and their details
export const BADGE_TYPES = {
  FOUNDING_MEMBER: {
    id: 'founding-member',
    name: 'Founding Member',
    // Public description (shown in tooltip)
    tooltipDescription: 'Founding Member',
    // Public description (shown in profile)
    publicDescription: 'One of the first 25 members of their chapter.',
    // Full description with discount info (for backend use only)
    description: 'One of the first 25 members of their chapter. Receives 50% discount on first year membership.',
    image: '/ap-badges/founding-member-badge-nobackground.png',
    discountPercentage: 50
  },
  VIP_MEMBER: {
    id: 'vip-member',
    name: 'VIP Member',
    tooltipDescription: 'VIP Member',
    publicDescription: 'One of the members #26-50 of their chapter.',
    description: 'One of the members #26-50 of their chapter. Receives 25% discount on first year membership.',
    image: '/ap-badges/vip-member-badge-nobackground.png',
    discountPercentage: 25
  },
  FIVE_YEAR_MEMBER: {
    id: '5-year-member',
    name: '5 Year Member',
    tooltipDescription: '5 Year Member',
    publicDescription: 'A member for 5 consecutive years.',
    description: 'A member for 5 consecutive years. Receives 25% off of 5 event registrations.',
    image: '/ap-badges/5-year-badge.png',
    eventDiscounts: 5,
    discountPercentage: 25
  },
  TEN_YEAR_MEMBER: {
    id: '10-year-member',
    name: '10 Year Member',
    tooltipDescription: '10 Year Member',
    publicDescription: 'A member for 10 consecutive years.',
    description: 'A member for 10 consecutive years. Receives 30% off of 10 event registrations.',
    image: '/ap-badges/10-year-member-badge-nobackground.png',
    eventDiscounts: 10,
    discountPercentage: 30
  },
  CHAPTER_PRESIDENT: {
    id: 'chapter-president',
    name: 'Chapter President',
    tooltipDescription: 'Chapter President',
    publicDescription: 'President of a local APN chapter.',
    description: 'President of a local APN chapter.',
    image: '/ap-badges/chapter-badge-president-nobackground.png'
  },
  AMBASSADOR: {
    id: 'ambassador',
    name: 'Ambassador',
    tooltipDescription: 'Ambassador',
    publicDescription: 'Member who has successfully referred multiple people to APN events or membership.',
    description: 'Member who has successfully referred multiple people to APN events or membership. Receives automatic discount on next membership or sponsorship package.',
    image: '/ap-badges/vip-member-badge-nobackground.png', // Temporary - using VIP badge until ambassador badge is added
    discountPercentage: 10
  }
};

// Helper function to get chapter-specific badge image
export const getChapterBadgeImage = (badgeType, chapter) => {
  if (!chapter) return null;

  const lowerChapter = chapter.toLowerCase();

  // Map of supported chapters and their badge images
  const chapterBadges = {
    'new york': {
      'chapter-president': '/ap-badges/chapter-badge-president-ny-nobackground.png'
    },
    'chicago': {
      'chapter-president': '/ap-badges/chapter-badge-president-chicago-nobackground.png'
    },
    'los angeles': {
      'chapter-president': '/ap-badges/chapter-badge-president-la-nobackground.png'
    },
    'boston': {
      'chapter-president': '/ap-badges/chapter-badge-president-nobackground.png'
    }
  };

  // If we have a specific badge for this chapter, return it
  if (chapterBadges[lowerChapter] && chapterBadges[lowerChapter][badgeType]) {
    return chapterBadges[lowerChapter][badgeType];
  }

  // Otherwise return the default badge
  return BADGE_TYPES[badgeType]?.image;
};

// Calculate membership duration in years
export const calculateMembershipYears = (memberSinceDate) => {
  if (!memberSinceDate) return 0;

  // Parse the member since date (assuming format like "May 2023")
  const parts = memberSinceDate.split(' ');
  if (parts.length !== 2) return 0;

  const month = parts[0];
  const year = parseInt(parts[1]);

  // Get current date
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Map month names to their numeric values
  const monthMap = {
    'January': 0, 'February': 1, 'March': 2, 'April': 3,
    'May': 4, 'June': 5, 'July': 6, 'August': 7,
    'September': 8, 'October': 9, 'November': 10, 'December': 11
  };

  const memberMonth = monthMap[month];

  // Calculate years of membership
  let years = currentYear - year;

  // Adjust if we haven't reached the anniversary month yet
  if (currentMonth < memberMonth) {
    years--;
  }

  return Math.max(0, years);
};

// Determine which badges a user has earned
export const determineUserBadges = (userData) => {
  if (!userData) return [];

  const badges = [];

  // Check for founding member badge
  if (userData.isFoundingMember || userData.email === 'admin@the-apn.com') {
    badges.push({
      ...BADGE_TYPES.FOUNDING_MEMBER,
      earnedDate: userData.memberSince
    });
  }

  // Check for VIP member badge
  if (userData.isVipMember || userData.email === 'admin@the-apn.com') {
    badges.push({
      ...BADGE_TYPES.VIP_MEMBER,
      earnedDate: userData.memberSince
    });
  }

  // Check for 5-year and 10-year membership badges
  const membershipYears = calculateMembershipYears(userData.memberSince);

  if (membershipYears >= 10) {
    badges.push({
      ...BADGE_TYPES.TEN_YEAR_MEMBER,
      earnedDate: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`
    });
  } else if (membershipYears >= 5) {
    badges.push({
      ...BADGE_TYPES.FIVE_YEAR_MEMBER,
      earnedDate: `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`
    });
  }

  // Check for chapter president badge
  if (userData.role === 'Chapter President') {
    const badge = {
      ...BADGE_TYPES.CHAPTER_PRESIDENT,
      name: `${userData.chapter} Chapter President`,
      tooltipDescription: `${userData.chapter} Chapter President`,
      earnedDate: userData.roleStartDate || userData.memberSince
    };

    // Get chapter-specific badge image if available
    const chapterBadgeImage = getChapterBadgeImage('chapter-president', userData.chapter);
    if (chapterBadgeImage) {
      badge.image = chapterBadgeImage;
    }

    badges.push(badge);
  }

  // Check for Ambassador badge
  if (userData.referralCount && userData.referralCount >= 5) {
    badges.push({
      ...BADGE_TYPES.AMBASSADOR,
      earnedDate: userData.ambassadorSince || new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }

  return badges;
};
